import { createContext, useContext } from "react";

import type { LoginRequest, AuthState, LoginResult, Site } from "../types/auth";
export interface AuthContextType extends AuthState {
  /**
   * True while the provider is probing the HttpOnly refresh cookie for a
   * session localStorage does not know about. Lives here rather than on
   * AuthState because the reducer rebuilds that wholesale on LOGOUT.
   */
  isBootstrapping: boolean;
  // Add or update a site in the nav selector's list (keyed by id) so a
  // create/edit in the zero panel reflects without re-login.
  upsertSite: (site: Site) => void;
  login: (credentials: LoginRequest) => Promise<LoginResult>;
  selectOrganization: (
    organizationId: string,
    pendingAuthenticationToken: string,
  ) => Promise<LoginResult>;
  confirmPasswordReset: (
    token: string,
    newPassword: string,
  ) => Promise<LoginResult>;
  sendPasswordReset: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Wipes the local session without calling sphinx: state, cached queries,
   * stored user/sites, the JWT client and the last-selected site. Use this,
   * not logout(), when a new session is about to be minted in the same
   * document — logout() POSTs with a stale bearer and asks sphinx to clear the
   * refresh cookies moments before we ask for new ones.
   */
  resetSession: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
